import { FormMultiSelector, Typography, Button, Form } from '@ui/components';
import { OnboardingSaveHighwaysDto, DayOfWeek } from '@interfaces/models';
import { RevertConfigurationDialog } from '@shared/components';
import { HighwaySectionDto } from '@interfaces/types';
import { useTranslation } from 'react-i18next';
import { FC } from 'react';

import {
  HighwaysConfigurationFormData,
  useHighwaysConfigurationForm,
} from './use-highways-configuration-form';
interface HighwaysConfigurationFormProps {
  defaultHighwaysConfiguration: OnboardingSaveHighwaysDto;
  highwaySections: HighwaySectionDto[];
  days: DayOfWeek[];
  onSubmit: (highwaysConfiguration: HighwaysConfigurationFormData) => void;
  onRevertChanges: () => void;
}

export const HighwaysConfigurationForm: FC<HighwaysConfigurationFormProps> = ({
  defaultHighwaysConfiguration,
  highwaySections,
  days,
  onSubmit,
  onRevertChanges,
}) => {
  const { t } = useTranslation();

  const {
    selectedHighwaysSectionsCustomValue,
    selectedDaysCustomValue,
    highwaysSectionOptions,
    daysOptions,
    form,
    sortHighwaysSections,
    sortDays,
  } = useHighwaysConfigurationForm({
    defaultValues: defaultHighwaysConfiguration,
    highwaySections,
    days,
  });

  return (
    <div>
      <Form {...form}>
        <section className="space-y-6 mt-8">
          <div>
            <Typography variant="labelMediumMedium" className="text-weak mb-2">
              {t('COMPANY_POLICY.HIGHWAYS_CONFIGURATION_SECTIONS')}:
            </Typography>
            <FormMultiSelector
              componentProps={{
                label: highwaysSectionOptions.length
                  ? selectedHighwaysSectionsCustomValue
                  : t('COMPANY_POLICY.HIGHWAYS_CONFIGURATION_SELECT_SECTIONS'),
                selectAllLabel: t('COMPANY_POLICY.HIGHWAYS_CONFIGURATION_SELECT_ALL_SECTIONS'),
              }}
              placeholder={t('COMPANY_POLICY.HIGHWAYS_CONFIGURATION_SELECT_SECTIONS')}
              disabled={!highwaysSectionOptions.length}
              sortFunction={sortHighwaysSections}
              options={highwaysSectionOptions}
              formControl={form.control}
              name="highwaySections"
            />
          </div>

          <div>
            <Typography variant="labelMediumMedium" className="text-weak mb-2">
              {t('COMPANY_POLICY.HIGHWAYS_CONFIGURATION_DAYS')}:
            </Typography>
            <FormMultiSelector
              componentProps={{
                label: selectedDaysCustomValue,
                selectAllLabel: t('COMPANY_POLICY.HIGHWAYS_CONFIGURATION_SELECT_ALL_DAYS'),
              }}
              placeholder={t('COMPANY_POLICY.HIGHWAYS_CONFIGURATION_SELECT_DAYS')}
              disabled={!daysOptions.length}
              formControl={form.control}
              sortFunction={sortDays}
              options={daysOptions}
              name="days"
            />
          </div>

          <div className="flex justify-between items-center mt-12">
            <Button onClick={form.handleSubmit(onSubmit)} type="button">
              {t('COMPANY_POLICY.SAVE')}
            </Button>

            <RevertConfigurationDialog
              description={t('COMPANY_POLICY.HIGHWAYS_CONFIGURATION_REVERT_CHANGES_DESCRIPTION')}
              title={t('COMPANY_POLICY.HIGHWAYS_CONFIGURATION_REVERT_CHANGES_TITLE')}
              onRevertChanges={onRevertChanges}
            />
          </div>
        </section>
      </Form>
    </div>
  );
};
